import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import "./ShortUrlSection.css";

const baseUrl = "https://apiportal.mbsays.com/public/api";

const ShortUrlSection = () => {
  const [urlList, setUrlList] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [loading, setLoading] = useState(true);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // ✅ alert yerine ekranda gösterilecek hata
  const [errorMsg, setErrorMsg] = useState("");

  const fmt = (d) => (d ? new Date(d).toLocaleString("tr-TR") : "-");

  // ✅ Kısa linki portal domainiyle gösteriyoruz
  const shortDomainHint = useMemo(() => {
    return "https://portal.mbsays.com/s/";
  }, []);

  // ✅ token/headers her request'te güncel okunsun
  const getHeaders = () => {
    const token = sessionStorage.getItem("authToken");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  // ✅ güvenli JSON okuma (bazen API HTML döner, json parse patlamasın)
  const safeJson = async (res) => {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return { _raw: text };
    }
  };

  const fetchUrls = async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const res = await fetch(`${baseUrl}/short-urls`, { headers: getHeaders() });
      const data = await safeJson(res);

      if (!res.ok) {
        const msg =
          (data && data.message) ||
          `Liste alınamadı (${res.status})`;
        throw new Error(msg);
      }

      const list = Array.isArray(data) ? data : [];

      const mapped = list.map((x) => ({
        id: x.id,
        originalUrl: x.original_url,
        // backend short_url vermezse portal domainli fallback
        shortUrl: x.short_url || `${shortDomainHint}${x.code}`,
        code: x.code,
        date: x.created_at,
        clicks: x.clicks ?? 0,
        lastClickedAt: x.last_clicked_at,
      }));

      setUrlList(mapped);
    } catch (e) {
      console.error("Short url list error:", e);
      setErrorMsg(e?.message || "Liste alınamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addUrl = async (e) => {
    e.preventDefault();
    const trimmed = newUrl.trim();
    if (!trimmed) return;

    setErrorMsg("");

    try {
      const res = await fetch(`${baseUrl}/short-urls`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ original_url: trimmed }),
      });

      const data = await safeJson(res);

      if (!res.ok) {
        const msg =
          (data && data.message) ||
          `Kısaltma başarısız (${res.status})`;
        throw new Error(msg);
      }

      setNewUrl("");
      await fetchUrls();
    } catch (e2) {
      console.error("Short url add error:", e2);
      setErrorMsg(e2?.message || "Kısaltma başarısız");
    }
  };

  const deleteUrl = async (id) => {
    if (!window.confirm("Bu kısa linki silmek istediğine emin misin?")) return;

    setErrorMsg("");

    try {
      const res = await fetch(`${baseUrl}/short-urls/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });

      const data = await safeJson(res);

      if (!res.ok) {
        const msg =
          (data && data.message) ||
          `Silinemedi (${res.status})`;
        throw new Error(msg);
      }

      await fetchUrls();

      if (selectedRow?.id === id) {
        setSelectedRow(null);
        setModalIsOpen(false);
      }
    } catch (e) {
      console.error("Short url delete error:", e);
      setErrorMsg(e?.message || "Silinemedi");
    }
  };

  const openModal = (row) => {
    setSelectedRow(row);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setSelectedRow(null);
    setModalIsOpen(false);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // istersen bunu da alert yerine küçük toast yaparız, şimdilik kalsın:
      alert("Kopyalandı ✅");
    } catch (e) {
      console.error("Copy error:", e);
      setErrorMsg("Kopyalanamadı (tarayıcı izin vermedi).");
    }
  };

  return (
    <div className="shorturl-section">
      <h2>Kısa URL Oluştur</h2>

      {errorMsg && (
        <div
          style={{
            margin: "12px 0",
            padding: 10,
            border: "1px solid #f5c2c7",
            borderRadius: 8,
            wordBreak: "break-word",
          }}
        >
          {errorMsg}
        </div>
      )}

      <div className="add-url-form">
        <form onSubmit={addUrl}>
          <input
            type="text"
            placeholder="URL giriniz (https://...)"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
          />
          <button type="submit">Kısalt</button>
        </form>
      </div>

      {loading ? (
        <p className="loading-text">Yükleniyor…</p>
      ) : (
        <table className="shorturl-table">
          <thead>
            <tr>
              <th>Orijinal URL</th>
              <th>Kısa URL</th>
              <th>Tıklanma</th>
              <th>Eklenme</th>
              <th>Detay</th>
            </tr>
          </thead>
          <tbody>
            {urlList.map((row) => (
              <tr key={row.id}>
                <td style={{ wordBreak: "break-word" }}>{row.originalUrl}</td>

                <td style={{ wordBreak: "break-word" }}>
                  <a href={row.shortUrl} target="_blank" rel="noreferrer">
                    {row.shortUrl}
                  </a>
                </td>

                <td>{row.clicks}</td>
                <td>{fmt(row.date)}</td>
                <td>
                  <button onClick={() => openModal(row)}>Detay</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="URL Detay"
        className="modal"
        overlayClassName="modal-overlay"
        ariaHideApp={false}
      >
        {selectedRow && (
          <div>
            <h2>URL Detayları</h2>

            <p>
              <strong>Orijinal URL:</strong>{" "}
              <span style={{ wordBreak: "break-word" }}>
                {selectedRow.originalUrl}
              </span>
            </p>

            <p>
              <strong>Kısa URL:</strong>{" "}
              <a href={selectedRow.shortUrl} target="_blank" rel="noreferrer">
                {selectedRow.shortUrl}
              </a>
            </p>

            <p>
              <strong>Tıklanma:</strong> {selectedRow.clicks}
            </p>

            <p>
              <strong>Eklenme:</strong> {fmt(selectedRow.date)}
            </p>

            <p>
              <strong>Son tıklanma:</strong> {fmt(selectedRow.lastClickedAt)}
            </p>

            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 16,
                flexWrap: "wrap",
              }}
            >
              <button onClick={() => copyToClipboard(selectedRow.shortUrl)}>
                Kısa Linki Kopyala
              </button>

              <button onClick={() => copyToClipboard(selectedRow.originalUrl)}>
                Orijinal Linki Kopyala
              </button>

              <button
                className="delete-btn"
                onClick={() => deleteUrl(selectedRow.id)}
              >
                Sil
              </button>
            </div>
          </div>
        )}

        <button className="modal-close" onClick={closeModal}>
          x
        </button>
      </Modal>
    </div>
  );
};

export default ShortUrlSection;
